/**
 * Aufrufe der Auditansicht -- F5-02.
 *
 * **Nur Lesen.** Es gibt hier keine schreibende Funktion, und das ist keine
 * Sparsamkeit: `audit_log` ist nachtraeglich nicht aenderbar (unverhandelbare
 * Regel 4 aus CLAUDE.md). Eine Funktion, die etwas anderes verspraeche, waere
 * ein Versprechen, das der Server gleich darauf braeche.
 */

import type { components } from '../../api/schema';
import { download, request } from '../../lib/apiClient';

// ``AuditListEntry`` und nicht ``AuditEntryRead``: Den gibt es bereits
// fuer die aeltere Protokollansicht unter ``features/privacy``. Warum das
// zwei Namen sein muessen, steht im Modell selbst.
export type AuditEntry = components['schemas']['AuditListEntry'];
export type AuditEntryDetail = components['schemas']['AuditEntryDetail'];
export type AuditPage = components['schemas']['AuditPage'];
export type AuditModule = components['schemas']['AuditModuleRead'];
export type AuditActor = components['schemas']['AuditActorRead'];
export type AuditSummary = components['schemas']['AuditSummaryRead'];
export type FieldChange = components['schemas']['FieldChangeRead'];

/**
 * Die Einschraenkungen der Liste.
 *
 * Modul, Bearbeiter und Aktion nehmen **mehrere** Werte; zwischen den Gruppen
 * gilt UND. So fragt man auch danach: "Was hat diese Woche jemand aus dem
 * Team an Tickets und Kunden geaendert?"
 */
export interface AuditFilters {
  /** Objektarten -- nicht Modulschluessel. Die Zuordnung nimmt die Maske vor,
   *  weil der Server nach Objektart filtert und die Auswahl nach Modul zeigt. */
  object_type?: string[];
  action?: string[];
  actor_user_id?: string[];
  object_id?: string;
  from?: string;
  to?: string;
}

/**
 * Die Aktionen, die es gibt -- deckungsgleich mit `AuditAction` im Server.
 *
 * Als feste Liste und nicht aus dem Bestand: Anders als bei den Modulen ist
 * sie kurz, vollstaendig und aendert sich nur mit dem Server. Eine Abfrage
 * dafuer waere ein Aufruf fuer acht Zeichenketten.
 */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'read_sensitive',
  'login',
  'login_failed',
  'export',
  'anonymize',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Wie viele Eintraege eine Seite holt -- deckungsgleich mit `DEFAULT_LIMIT`. */
export const PAGE_SIZE = 50;

function toQuery(filters: AuditFilters): URLSearchParams {
  const query = new URLSearchParams();
  for (const value of filters.object_type ?? []) query.append('object_type', value);
  for (const value of filters.action ?? []) query.append('action', value);
  for (const value of filters.actor_user_id ?? []) query.append('actor_user_id', value);
  if (filters.object_id) query.set('object_id', filters.object_id);
  if (filters.from) query.set('from', filters.from);
  if (filters.to) query.set('to', filters.to);
  return query;
}

/**
 * Eine Seite der Liste.
 *
 * Ueber eine selbst gebaute Abfragezeichenkette und nicht ueber `query` des
 * Klienten: Die Mehrfachauswahl braucht denselben Namen mehrfach
 * (`object_type=ticket&object_type=customer`), und ein Objekt kann einen
 * Schluessel nur einmal tragen.
 */
export function fetchAuditEntries(
  filters: AuditFilters,
  { limit = PAGE_SIZE, offset = 0 }: { limit?: number; offset?: number } = {},
): Promise<AuditPage> {
  const query = toQuery(filters);
  query.set('limit', String(limit));
  query.set('offset', String(offset));
  return request<AuditPage>(`/audit/entries?${query.toString()}`);
}

/**
 * Derselbe Ausschnitt als CSV-Datei -- mit denselben Filtern wie die Liste.
 *
 * Wer ausgibt, will das, was er sieht. Die Datei traegt die Aenderungen
 * vollstaendig, die Liste bewusst nicht (siehe ``AuditPage``).
 */
export async function downloadAuditCsv(filters: AuditFilters): Promise<Blob> {
  const query = toQuery(filters);
  const { blob } = await download(`/audit/entries/export.csv?${query.toString()}`);
  return blob;
}

export function fetchAuditEntry(entryId: string): Promise<AuditEntryDetail> {
  return request<AuditEntryDetail>(`/audit/entries/${entryId}`);
}

export function fetchAuditModules(): Promise<AuditModule[]> {
  return request<AuditModule[]>('/audit/modules');
}

export function fetchAuditActors(): Promise<AuditActor[]> {
  return request<AuditActor[]>('/audit/actors');
}
